/** The one copy of what the app does when a database restore is running and when one fails.
 *
 * WHY THIS FILE EXISTS. Two pages restore a database: the snapshots page and the paste-a-JSON-file
 * page. They used to carry their own copy of this routine each, kept in step only by comments in one
 * pointing at the other ("same contract as the JSON restore page, see the long note there"). One of
 * the rules being copied is silently destructive if the two drift — see the `afterRemoteReplaced`
 * note below — and a drifted copy fails no test and no type check. Deferred finding df-b5da013af6dc
 * recorded that; this module is the single place those rules now live.
 *
 * Kept as plain functions with their dependencies passed in, the same way `restore-retry.ts` is, so
 * the rules can be unit-tested directly instead of only through a page. See `restore-failure.test.ts`.
 */

// Every Angular-provided dependency below is imported as a TYPE ONLY, and that is load-bearing rather
// than tidiness. Importing `providers/alert-controller` (or the logging service, or the database
// wrapper) for REAL pulls Angular's injector into this module at runtime, and the unit-test harness —
// plain mocha, no TestBed — cannot load that: doing so breaks the whole client unit-test run, not just
// this module's own tests. A type is erased at compile time, so the rules below stay executable by a
// test instead of only being matched as source text. The one consequence is that the OK button is
// handed in by the caller: this module cannot name the `AlertButtonRole` enum's value without
// importing it for real.
import type { AlertButton, AlertController } from 'providers/alert-controller';
import type { LoadingController } from 'providers/loading-controller';
import type { LoggingService } from 'services/logging.service';
import type { DatabaseWrapper } from 'services/database-wrapper';
import { RestoreAfterRemoteReplacedError } from 'services/restore-retry';
import { errorReportFields } from 'services/error-patterns';
import { getMyApp } from 'app/app-interface';
import { TranslatedString } from 'models/translated-string';

/** Everything a restore needs from the page that started it. */
export interface RestoreDeps {
  database: DatabaseWrapper;
  alertCtrl: AlertController;
  loadingCtrl: LoadingController;
  logging: LoggingService;
}

/** The translated strings a page shows for its own restore. Each page words these for what the user
 * asked for — a snapshot, or a pasted file — so they are supplied rather than shared. */
export interface RestoreFailureMessages {
  /** title of the alert shown when the restore failed */
  failedTitle: TranslatedString;
  /** body shown when the restore did NOT go through */
  failed: TranslatedString;
  /** body shown when the data WAS restored and only the cleanup afterwards failed */
  cleanupFailed: TranslatedString;
  /** the alert's only button, supplied whole — see the import note at the top of this file for why
   * this module cannot build it itself */
  okButton: AlertButton<void>;
}

export interface RunGuardedRestoreOptions {
  /** the documents to write over the database */
  docs: any[];
  /** what the loading cover says while the restore runs */
  loadingContent: TranslatedString;
}

export interface HandleRestoreFailureOptions {
  /** the error the restore rejected with */
  err: any;
  /** stable Sentry/Mixpanel condition name for this page's restore, e.g. `DbSnapshotRestoreFailed` */
  problemName: string;
  /** extra fields this page wants on the report, e.g. the snapshot's name or the document count */
  context: Record<string, any>;
  messages: RestoreFailureMessages;
}

/** Run a restore behind a loading cover, and return the error it failed with, or `null`.
 *
 * The cover comes down on EVERY path. It is a full-page overlay: leaving it up (which is what an
 * escaping rejection used to do) makes the page look busy forever and silently eats every click on
 * it. The dismiss itself is guarded too — if IT threw, the rejection would escape and land in the
 * unhandled-rejection handler that swallows these, losing the restore error with it.
 *
 * The error is RETURNED rather than rethrown so the caller decides what happens next while no cover
 * is on screen. */
export async function runGuardedRestoreAsync(
  deps: RestoreDeps,
  { docs, loadingContent }: RunGuardedRestoreOptions,
): Promise<any> {
  const loading = deps.loadingCtrl.create({ content: loadingContent });
  void loading.present();

  let restoreError: any = null;
  try {
    await deps.database.restoreFromDocsAsync(docs);
  } catch (err) {
    restoreError = err;
  } finally {
    // always take the loading cover down, and guard the dismiss itself
    try {
      await loading.dismiss();
    } catch (dismissErr) {
      // reported, never rethrown: an escaping rejection would take the restore error with it, and a
      // cover left up eats every click on the page (#5191)
      deps.logging.trackSilentProblem('RestoreLoadingDismissFailed', errorReportFields(dismissErr));
    }
  }

  return restoreError;
}

/** Tell the user the restore failed, then reload — or deliberately do NOT reload. See the contract on
 * `DatabaseWrapper.restoreFromDocsAsync`.
 *
 * We normally must reload: the restore cancelled sync and detached the remote database before it
 * started writing, and it does not put them back on failure. Returning quietly would leave the user on
 * a live-looking page with a dead database whose next "Restore" tap cannot work. The reload also heals
 * the remote if the delete pass got partway — the local database still holds the old docs, so the
 * fresh boot's sync pushes them back.
 *
 * We report it OURSELVES rather than through `trackAbnormalException`, and that is not a style choice.
 * A dropped request is a TypeError "Failed to fetch", which Sentry's `beforeSend` drops as transient
 * noise; `trackAbnormalException` then sees a silenced event and returns EARLY, before it would have
 * shown its toast. Routing this failure through it would show the user nothing at all — the exact
 * silence this whole routine exists to remove. So the alert is raised directly, and the telemetry goes
 * through `trackSilentProblem`, whose event is named (not fetch-shaped) and therefore survives that
 * filter.
 *
 * This function does not return on the reloading path: `reloadAppAsync` never returns. */
export async function handleRestoreFailureAsync(
  deps: RestoreDeps,
  { err, problemName, context, messages }: HandleRestoreFailureOptions,
): Promise<void> {
  // Two very different failures reach here, and they need OPPOSITE recoveries.
  const afterRemoteReplaced = err instanceof RestoreAfterRemoteReplacedError;

  deps.logging.trackSilentProblem(problemName, {
    ...context,
    message: err?.message,
    name: err?.name,
    afterRemoteReplaced,
  });

  await deps.alertCtrl.showAlertAsync({
    title: messages.failedTitle,
    message: afterRemoteReplaced ? messages.cleanupFailed : messages.failed,
    buttons: [messages.okButton],
  });

  // DO NOT RELOAD when the remote was already replaced. This looks like the same recovery but it is
  // the exact opposite. Reaching that branch means the local database SURVIVED, still holding the
  // pre-restore documents at higher rev generations than the rev-1 docs we just uploaded. A reload is
  // what re-attaches sync to it — and those old revisions would then win and overwrite the restore
  // that actually succeeded. Staying put is the SAFE state, not the broken one: the restore already
  // cancelled sync and detached the remote, so nothing here can push anything anywhere. The alert
  // tells the user to clear the app's storage, which is the only real repair.
  if (afterRemoteReplaced) {
    return;
  }

  // the mid-write case: the remote may be half-written, and the reload's sync IS the repair — the
  // still-intact local database pushes its documents back up.
  // NOT resetToRoot, unlike each page's success path: this reload heals a restore that did not replace
  // the data, so the remembered warm-resume address still points at data that is really there, and
  // dropping it would cost a mid-flow user their place for no reason (#5486).
  // through reloadAppAsync rather than a bare page reload, so the failure report filed above is
  // flushed before the page goes away.
  await getMyApp().reloadAppAsync({ resetToRoot: false });
}
